import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { MouseEvent } from 'react';
import { Breadcrumb } from '../../../../components/Atoms/BreadCrumb';
import { Loader } from '../../../../components/Atoms/Loader';
import { CTA_KEY } from '../../../../components/CTA/constants';
import TableOfContents from '../../../../components/Common/TableOfContents';
import { ContentBankIndividualsComponent } from '../../../../components/IfscCode/ContentBankIndividuals';
import SearchIFSC from '../../../../components/IfscCode/SearchIFSC';
import IFSCDataService from '../../../../components/IfscCode/Services/ifsc.service';
import { BankDetail } from '../../../../components/IfscCode/types';
import { ClearInNavbar } from '../../../../components/Navbar/ClearNavbar';
import {
  toTitleCase,
  urlformatter,
} from '../../../../components/Utils/CommonUtils';
import { TopCTA } from '../../../../components/cms/layout/components';
import { b2cData } from '../../../../components/cms/layout/components/header';
import { Size } from '../../../../constants/commonEnums';
import { getBreadCrumbBank } from '../../../../constants/componentStaticData';
import {
  DEPLOYMENT_DOMAIN,
  ENABLE_IFSC_PAGES,
} from '../../../../env-vars.constants';
import { Meta } from '../../../../layout/Meta';
import { returnNoPaths } from '../../../../utils/UtilFunctions';
import { getCachedCTAData } from '../../../../utils/cta-cache';
import { staticPropsWrapper } from '../../../../utils/static-pages/utils';
import { handleTrack } from '../../../../utils/tracker';

type BREADCRUMB_TYPE = {
  title: string | undefined | string[];
  link: string | null;
};

interface BankIndividualsInterface {
  bank_name: string;
  bank_logo: string;
}

interface IndiVidualBankPropsInterface {
  indiVidualBank: BankIndividualsInterface;
  top_cta: string;
}

const BankIndividuals = ({
  indiVidualBank,
  top_cta,
}: IndiVidualBankPropsInterface): JSX.Element => {
  const router = useRouter();
  // Render post...
  if (router.isFallback) {
    return (
      <div className="loaderParentDiv">
        <Loader size={Size.MEDIUM} />
      </div>
    );
  }

  const { bank_name, bank_logo } = indiVidualBank;
  const titleBankName = toTitleCase(bank_name);

  const BREADCRUMB: Array<BREADCRUMB_TYPE> = [
    { title: 'Home', link: '/' },
    { title: 'IFSC code', link: '/ifsc-code/' },
    { title: titleBankName, link: null },
  ];

  const bankIndividualsContents = [
    {
      id: 'ifsccode',
      header: `${titleBankName} IFSC Codes`,
    },
    {
      id: 'ifsccodesearch',
      header: `${titleBankName} IFSC Code Search`,
    },
    {
      id: 'steps',
      header: `Steps to Open ${titleBankName} Account`,
    },
    {
      id: 'faqs',
      header: 'FAQs',
    },
  ];

  const captureEvent = (e: MouseEvent<Element, MouseEvent>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };

  return (
    <div>
      <ClearInNavbar data={b2cData} />
      <Meta
        title={`IFSC Code Search - Find ${toTitleCase(
          bank_name
        )} IFSC Codes and Address of All Branches in India - ${toTitleCase(
          bank_name
        )}`}
        description={`IFSC Code Search - ${toTitleCase(
          bank_name
        )} IFSC Codes and address of any ${toTitleCase(
          bank_name
        )} branch in India. Get ${toTitleCase(
          bank_name
        )} IFSC (Indian Financial System Code)`}
        canonical={`https://cleartax.in/ifsc-code/${urlformatter(bank_name)}/`}
        ampURL={`https://cleartax.in/ifsc-code/amp/${urlformatter(bank_name)}/`}
      />
      <TopCTA CTA={top_cta} onClickCB={captureEvent} />
      <div className="wg-container lg:hidden xl:hidden md:hidden">
        <div className="mx-auto my-8 sm:mx-0">
          <Breadcrumb dataSet={BREADCRUMB} />
        </div>
        <div className="py-6">
          <SearchIFSC />
        </div>
        <div className="text-center py-6">
          <img
            src={bank_logo}
            className="mx-auto"
            alt="bank_logo"
            width="150"
            height="150"
          />
          <h1 className="mt-2 text-s-32">{titleBankName.toUpperCase()}</h1>
        </div>
      </div>
      <div className="wg-container py-12 flex mx-auto text-font-500 sm:flex-col-reverse">
        <div className="w-3/4 pr-12 sm:pr-0 sm:w-full sm:mt-6">
          <div className="sm:hidden">
            <div className="mx-auto my-8">
              <Breadcrumb dataSet={BREADCRUMB} />
            </div>
            <div className="text-center py-6">
              <img
                src={bank_logo}
                className="mx-auto"
                alt="bank_logo"
                width="250"
                height="150"
              />
              <h1 className="mt-2 text-s-32">{titleBankName.toUpperCase()}</h1>
            </div>
            <div className="py-12">
              <SearchIFSC />
            </div>
          </div>
          <ContentBankIndividualsComponent bankName={titleBankName} />
        </div>

        <div className="w-1/4 pl-12 py-8 text-font-500 sm:pl-0 sm:w-full sm:py-0">
          <TableOfContents data={bankIndividualsContents} />
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getBreadCrumbBank(bank_name)),
        }}
      ></script>
    </div>
  );
};

export default BankIndividuals;

export const getStaticPaths: GetStaticPaths = async () => {
  if (DEPLOYMENT_DOMAIN !== 'cleartax.in' || ENABLE_IFSC_PAGES !== 'true') {
    return returnNoPaths();
  }
  try {
    const response = await IFSCDataService.getAllBankDetails();

    if (response.status !== 200 || !response.data) {
      return returnNoPaths();
    }

    let END_INDEX = 0;
    if (
      Number(process.env.IFSC_BUILD_LAST_INDEX) === 0 ||
      Number(process.env.IFSC_BUILD_LAST_INDEX) > response.data.length
    ) {
      END_INDEX = response.data.length - 1;
    } else {
      END_INDEX = Number(process.env.IFSC_BUILD_LAST_INDEX);
    }

    const paths = response?.data
      .slice(Number(process.env.IFSC_BUILD_START_INDEX), END_INDEX)
      .map((bankDetail: BankDetail) => {
        return {
          params: {
            bankIndividuals: urlformatter(bankDetail.bank_slug),
          },
        };
      });

    return {
      paths,
      fallback: false,
    };
  } catch (error: unknown) {
    console.error(
      'Error generating static paths:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    return returnNoPaths();
  }
};

export const getStaticProps: GetStaticProps = staticPropsWrapper(
  __filename,
  async (context: { params?: { bankIndividuals?: string } }) => {
    const { params } = context;

    if (params?.bankIndividuals) {
      try {
        const res = await IFSCDataService.getBankIndividuals(
          params.bankIndividuals as string
        );
        let responseData;
        if (res.status === 200) {
          responseData = await res.data;
        }
        // Use cached CTA data to avoid repeated API calls during build
        const cta = await getCachedCTAData(CTA_KEY.IFSC_CTA);

        return {
          props: {
            indiVidualBank: responseData,
            top_cta: cta?.cta_html ?? '',
          },
        };
      } catch (error: unknown) {
        console.log(
          error instanceof Error ? error.message : 'Unknown error',
          'error'
        );
        return {
          notFound: true,
        };
      }
    } else {
      return {
        notFound: true,
      };
    }
  }
);
